/**
 * Existing authenticated cache-first GETs only.
 * Does not change request parameters, add writes, poll, or force-refresh.
 */
import api from "@/lib/api";

export const LIVE_GETS = {
  tickerData: {
    id: "tickerData",
    method: "GET",
    path: (symbol) => `/ticker/${symbol}/data`,
    params: { profile: "proposed", strike_mode: "proposed", strike_engine_mode: "delta" },
    tabs: ["overview", "regime", "options", "technicals", "fundamentals", "earnings"],
  },
  regimeHistory: {
    id: "regimeHistory",
    method: "GET",
    path: (symbol) => `/regime/${symbol}/history`,
    params: { days: 180 },
    tabs: ["overview", "regime"],
  },
  rankedStrategies: {
    id: "rankedStrategies",
    method: "GET",
    path: (symbol) => `/regime/ranked-strategies/${symbol}`,
    params: { dte: 30 },
    tabs: ["options"],
  },
  regimeBacktestContext: {
    id: "regimeBacktestContext",
    method: "GET",
    path: (symbol) => `/ticker/${symbol}/regime-backtest-context`,
    params: { dte: 30 },
    tabs: ["options"],
  },
  tickerTalk: {
    id: "tickerTalk",
    method: "GET",
    path: (ticker) => `/ticker-talk/${ticker}`,
    params: {},
    tabs: ["talk"],
  },
  shadowMoney: {
    id: "shadowMoney",
    method: "GET",
    path: (ticker) => `/shadow-money/${ticker}`,
    params: {},
    tabs: ["ownership"],
  },
};

function isCanceled(err) {
  return err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError";
}

function headerHint(headers) {
  if (!headers) return null;
  return headers["x-cache"] || headers["x-cache-status"] || null;
}

export function createRequestLog() {
  const entries = [];
  return {
    entries,
    record(entry) {
      entries.push(entry);
      return entry;
    },
  };
}

export async function liveGet(spec, symbol, { signal, tab, log, extraParams } = {}) {
  const started = Date.now();
  const path = spec.path(symbol);
  const params = { ...spec.params, ...(extraParams || {}) };
  const rec = {
    method: "GET",
    path: `/api${path}`,
    params,
    tab: tab || spec.tabs[0],
    resource: spec.id,
    startedAt: new Date(started).toISOString(),
    finishedAt: null,
    durationMs: null,
    httpStatus: null,
    ok: false,
    canceled: false,
    write: false,
    externalProvider: false,
    cacheHeader: null,
    error: null,
  };
  log?.record(rec);
  try {
    const res = await api.get(path, { params, signal });
    rec.finishedAt = new Date().toISOString();
    rec.durationMs = Date.now() - started;
    rec.httpStatus = res.status;
    rec.ok = true;
    rec.cacheHeader = headerHint(res.headers);
    return { data: res.data, meta: rec };
  } catch (err) {
    rec.finishedAt = new Date().toISOString();
    rec.durationMs = Date.now() - started;
    rec.canceled = isCanceled(err);
    rec.httpStatus = err?.response?.status ?? null;
    rec.error = rec.canceled
      ? "canceled"
      : (err?.response?.data?.detail || err?.message || "request failed");
    if (typeof rec.error === "object") rec.error = JSON.stringify(rec.error);
    if (rec.canceled) throw err;
    throw Object.assign(err, { liveMeta: rec });
  }
}

export function getTickerData(symbol, opts) {
  return liveGet(LIVE_GETS.tickerData, symbol, opts);
}

export function getRegimeHistory(symbol, opts) {
  return liveGet(LIVE_GETS.regimeHistory, symbol, opts);
}

export function getRankedStrategies(symbol, opts) {
  return liveGet(LIVE_GETS.rankedStrategies, symbol, opts);
}

export function getRegimeBacktestContext(symbol, strategy, opts) {
  return liveGet(LIVE_GETS.regimeBacktestContext, symbol, {
    ...opts,
    extraParams: { strategy, dte: 30 },
  });
}

export function getTickerTalk(ticker, opts) {
  return liveGet(LIVE_GETS.tickerTalk, ticker, opts);
}

export function getShadowMoney(ticker, opts) {
  return liveGet(LIVE_GETS.shadowMoney, ticker, opts);
}

export function tabNeeds(tab) {
  if (tab === "overview" || tab === "regime") return ["tickerData", "regimeHistory"];
  if (tab === "options") return ["tickerData", "rankedStrategies", "regimeBacktestContext"];
  if (tab === "talk") return ["tickerData", "tickerTalk"];
  if (tab === "ownership") return ["tickerData", "shadowMoney"];
  return ["tickerData"];
}
